'use client';

import dynamic from 'next/dynamic';
import { useCallback, useEffect, useState } from 'react';

import { useResponsiveDialogStore } from '@/stores/DrawerStore';

const ResponsiveDialog = dynamic(
  () =>
    import('../../organism/ResponsiveDialog').then((mod) => ({
      default: mod.ResponsiveDialog,
    })),
  { ssr: false },
);

/**
 * 데스크톱에서는 모달, 모바일에서는 드로워로 표시\
 * openResponsiveDialog를 이용해서 간결하게 사용 가능
 *
 * @example
 *
 * ```tsx
 * import { openResponsiveDialog } from '@/stores/DrawerStore';
 *
 * openResponsiveDialog({
 *   component: HouseholdConsent,
 * });
 * ```
 */
export const ResponsiveDialogContainer = () => {
  const { currentDialog, isOpen, closeDialog } = useResponsiveDialogStore();
  const [isMounted, setIsMounted] = useState(false);

  const handleClose = useCallback(() => {
    currentDialog?.props?.onClose?.();
    closeDialog();
  }, [currentDialog, closeDialog]);

  // 클라이언트 사이드에서만 Portal을 렌더링하기 위한 마운트 체크
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // ESC 키로 Drawer 닫기
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleClose]);

  // 브라우저 뒤로가기 버튼으로 Drawer 닫기
  useEffect(() => {
    if (!isOpen) return;

    // Drawer가 열릴 때 히스토리에 상태 추가
    const state = { drawerOpen: true };
    window.history.pushState(state, '');

    const handlePopState = (event: PopStateEvent) => {
      // 뒤로가기 시 Drawer 닫기
      if (isOpen) {
        event.preventDefault();
        handleClose();
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isOpen, handleClose]);

  if (!isMounted || !currentDialog) return null;

  const Component = currentDialog.component;

  return (
    <ResponsiveDialog
      opened={isOpen}
      onClose={handleClose}
      title={currentDialog.title}
      duration={currentDialog.duration}
      className={currentDialog.className}
      modalHeader={currentDialog.modalHeader}
      drawerNoPadding={currentDialog.drawerNoPadding}
      hideDrawerHeader={currentDialog.hideDrawerHeader}
      drawerType={currentDialog.drawerType}
      forceModal={currentDialog.forceModal}
    >
      <Component {...currentDialog.props} onClose={handleClose} />
    </ResponsiveDialog>
  );
};
